<template>
  <div class="card">
    <div class="card__head">
      <div>
        <h2 class="card__title">{{ t("installs.title") }}</h2>
        <p class="card__sub">{{ t("installs.lede") }}</p>
      </div>
      <button type="button" class="btn btn--secondary btn--sm" :disabled="loading" @click="load">{{ t("common.refresh") }}</button>
    </div>

    <div v-if="error" class="notice notice--danger"><div class="notice__body">{{ error }}</div></div>
    <div v-else-if="loading" class="row"><span class="spinner" aria-hidden="true"></span><span class="muted small">{{ t("common.loading") }}</span></div>
    <p v-else-if="!rows.length" class="muted small">{{ t("installs.empty") }}</p>

    <template v-else>
      <div class="row row--wrap installs__summary">
        <span class="badge badge--success">{{ t("installs.activeCount", { count: active }) }}</span>
        <span class="badge">{{ t("installs.totalCount", { count: rows.length }) }}</span>
      </div>
      <div class="table__wrap">
        <table class="table">
          <thead>
            <tr>
              <th>{{ t("testers.installation") }}</th>
              <th>{{ t("common.status") }}</th>
              <th>{{ t("installs.scopeMode") }}</th>
              <th>{{ t("editor.scopes") }}</th>
              <th>{{ t("installs.installed") }}</th>
              <th>{{ t("installs.uninstalled") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.id || `${r.installation_id}:${r.install_ref}`">
              <td class="small">
                {{ installationName(installations, r.installation_id) }}
                <span v-if="r.dev_mode" class="badge badge--brand">{{ t("installs.devMode") }}</span>
              </td>
              <td><span :class="['badge', r.status === 'active' ? 'badge--success' : r.status === 'suspended' ? 'badge--danger' : '']">{{ r.status }}</span></td>
              <td class="small">{{ r.scope_mode || "—" }}</td>
              <td class="small muted">{{ (r.granted_scopes || []).join(", ") }}</td>
              <td class="small muted">{{ formatDate(r.installed_at, false) }}</td>
              <td class="small muted">{{ r.uninstalled_at ? formatDate(r.uninstalled_at, false) : "—" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="xs muted" style="margin-top: var(--space-3)">{{ t("installs.privacy") }}</p>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { api } from "../../lib/api";
import { useErrorText, formatDate } from "../../lib/errors";
import { loadInstallations, installationName } from "../../lib/installations";

const props = defineProps({ appId: { type: String, required: true } });
const { t } = useI18n();
const errorText = useErrorText();

const rows = ref([]);
const installations = ref([]);
const loading = ref(true);
const error = ref("");
const active = computed(() => rows.value.filter((r) => r.status === "active").length);

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const [data, list] = await Promise.all([api.installs(props.appId), loadInstallations().catch(() => [])]);
    rows.value = Array.isArray(data) ? data : [];
    installations.value = list;
  } catch (e) {
    error.value = errorText(e);
  } finally {
    loading.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.installs__summary { margin-bottom: var(--space-3); }
</style>
